import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ButtonType, HeroButton } from "@gaming/xds-react-js";

import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getTicketingCreateTeamError,
  getTicketingIsCreatingTeam,
  getTicketingSelectedAccessId,
} from "../../redux/selectors/ticketingSelectors";
import { useTicketingStrings } from "../TicketingPage/ticketingStrings";
import * as styles from "./CreateTeamModal.module.css";

type CreateTeamModalProps = {
  sellerName: string;
  onClose: () => void;
};

export const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ sellerName, onClose }) => {
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const accessId = useSelector(getTicketingSelectedAccessId);
  const isCreating = useSelector(getTicketingIsCreatingTeam);
  const createError = useSelector(getTicketingCreateTeamError);

  const [teamName, setTeamName] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();
  const modalRef = useRef<HTMLDivElement>(null);

  // Prevent background scrolling while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Focus trap
  useEffect(() => {
    const container = modalRef.current;
    if (!container) {
      return;
    }

    const focusableSelector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () =>
      Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        el => el.offsetParent !== null
      );

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") {
        return;
      }
      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    const focusable = getFocusableElements();
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(() => {
    const trimmed = teamName.trim();
    if (!trimmed) {
      setValidationError(strings.ticketCreateTeamNameRequired);
      return;
    }
    if (trimmed.length > 100) {
      setValidationError(strings.ticketCreateTeamNameTooLong);
      return;
    }
    setValidationError(undefined);
    if (accessId) {
      dispatch(
        ticketingActions.createTeam.start({
          accessId,
          teamName: trimmed,
          memberContactIds: [],
        })
      );
    }
  }, [
    teamName,
    accessId,
    dispatch,
    strings.ticketCreateTeamNameRequired,
    strings.ticketCreateTeamNameTooLong,
  ]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const hintTemplate = strings.ticketCreateTeamNameHint || "";
  const nameHint = hintTemplate.replace("[[sellerName]]", sellerName || "—");

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        className={styles.modal}
        role="dialog"
        aria-labelledby="create-team-modal-title"
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="create-team-modal-title" className={styles.title}>
            {strings.ticketCreateTeamModalTitle}
          </h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Team Name */}
          <label className={styles.fieldLabel}>{strings.ticketCreateTeamNameLabel}</label>
          <input
            className={styles.nameInput}
            type="text"
            value={teamName}
            maxLength={100}
            onChange={e => {
              setTeamName(e.target.value);
              if (validationError) {
                setValidationError(undefined);
              }
            }}
          />
          <p className={styles.fieldHint}>{nameHint}</p>

          {(validationError || createError) && (
            <div className={styles.errorMessage}>{validationError ?? createError}</div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <HeroButton
            as="button"
            buttonType={ButtonType.Secondary}
            className={styles.cancelButton}
            onClick={onClose}
            disabled={isCreating}
          >
            {strings.ticketCreateTeamCancelButton}
          </HeroButton>
          <HeroButton
            as="button"
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={isCreating}
          >
            {isCreating
              ? strings.ticketCreateTeamCreatingButton
              : strings.ticketCreateTeamSubmitButton}
          </HeroButton>
        </div>
      </div>
    </div>
  );
};
